import React, { memo, useState, useEffect } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row, Spinner, Card, CardBody, InputGroup, InputGroupAddon, InputGroupText, Badge, UncontrolledCollapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faChevronDown, faChevronUp, faCode, faServer, faCog, faFileAlt, faCheck, faInfoCircle, faLock, faUnlock, faTimes } from "@fortawesome/free-solid-svg-icons";
import instance from "../../../axiosInstance";
import { useUserContext } from "../../../context/security/users";
import useError from "../../../hooks/useError";
import { toastr } from "react-redux-toastr";
import Select from "react-select";

const serviceOptionsByCaseType = {
    rtadriver: ["personalInjury", "hire", "repair", "storage", "recovery", "inspection", "outlay", 'ibClaim'],
    rtapassenger: ["personalInjury", 'ibClaim'],
    rtamib: ["personalInjury", "hire", "repair", 'ibClaim'],
    "s&t": ["personalInjury"],
    publicliability: ["personalInjury"],
    occupiersliability: ["personalInjury"],
    workaccident: ["personalInjury"],
    housingdisrepair: ["personalInjury"],
    rtamotorcycle: ["personalInjury", "hire", "repair", "storage", "recovery", "inspection", "outlay", 'ibClaim'],
    rtaportal: ["personalInjury", "hire", "repair", "storage", "recovery", "inspection", "outlay", 'ibClaim'],
    rtanondriver: ["personalInjury", "hire", "repair", "storage", "recovery", "inspection", "outlay", 'ibClaim'],
};

const availableApis = [
    {
        id: "case_create",
        name: "Case Creation",
        method: "POST",
        url: "/api/v1/create-case",
        description: "Create new cases with client and vehicle details",
        color: "#4CAF50",
        icon: faServer,
        requiredParams: ["case_type", "services"]
    },
      {
        id: "case_view",
        name: "Case View",
        method: "GET",
        url: "/api/v1/case-view",
        description: "View case details",
        color: "#2196F3",
        icon: faCog,
        // requiredParams: ["case_id"]
      },
    //   {
    //     id: "case-status",
    //     name: "Case Status",
    //     method: "GET",
    //     url: "/case/status",
    //     description: "Get case status and progress",
    //     color: "#FF9800",
    //     icon: faInfoCircle,
    //     requiredParams: ["case_id"]
    //   }
];

const UserAPI = memo(() => {
    const { userId } = useUserContext();
    const { handleError } = useError();
    const [generatedToken, setGeneratedToken] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [caseTypes, setCaseTypes] = useState([]);
    const [selectedCaseType, setSelectedCaseType] = useState(null);
    const [selectedServices, setSelectedServices] = useState([]);
    const [activeApi, setActiveApi] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [selectedApis, setSelectedApis] = useState([]);
    const [apiParameters, setApiParameters] = useState({});

    const serviceTypesOptions = [
        { id: "personalInjury", label: "Personal Injury" },
        { id: "hire", label: "Hire" },
        { id: "repair", label: "Repair" },
        { id: "storage", label: "Storage" },
        { id: "recovery", label: "Recovery" },
        { id: "inspection", label: "Inspection" },
        { id: "outlay", label: "Outlay" },
        { id: "ibClaim", label: "IB Claim" },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                // Fetch case types
                const caseTypesResponse = await instance.get("lookups/caseTypes");
                const options = caseTypesResponse.data.data.map((caseType) => ({
                    value: caseType.toLowerCase().replace(/\s+/g, ""),
                    label: caseType,
                }));
                setCaseTypes(options);

                // Fetch user API settings
                const userApiResponse = await instance.get(`/user/getUserAPI/${userId}`);
                const userApiData = userApiResponse.data.data;

                // Initialize selected APIs and parameters
                const initialSelectedApis = [];
                const initialApiParameters = {};

                userApiData.forEach(api => {
                    if (api.api_type && api.api_type !== '') {  // Only add if api_type exists and is not empty
                        initialSelectedApis.push(api.api_type);
                        if (api.parameters) {
                            initialApiParameters[api.api_type] = JSON.parse(api.parameters);
                        }
                    }
                });

                setSelectedApis(initialSelectedApis);
                setApiParameters(initialApiParameters);

                // Set case creation specific parameters if they exist
                const caseCreateApi = userApiData.find(api => api.api_type === 'case_create');
                if (caseCreateApi) {
                    const parameters = JSON.parse(caseCreateApi.parameters);
                    // Set case type
                    const caseTypeOption = options.find(opt => opt.value === parameters?.case_type);
                    if (caseTypeOption) {
                        setSelectedCaseType(caseTypeOption);
                    }

                    // Set services
                    if (Array.isArray(parameters?.services)) {
                        setSelectedServices(parameters?.services);
                    }
                }
            } catch (err) {
                console.log(err);
                handleError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [userId]);

    const handleApiSelection = (apiId) => {
        setSelectedApis(prev => {
            if (prev.includes(apiId)) {
                // Remove API and its parameters
                const newApis = prev.filter(id => id !== apiId);
                setApiParameters(prevParams => {
                    const newParams = { ...prevParams };
                    delete newParams[apiId];
                    return newParams;
                });
                return newApis;
            } else {
                // Add API
                return [...prev, apiId];
            }
        });
    };

    const handleCaseTypeChange = (value) => {
        setSelectedCaseType(value);
        setSelectedServices([]); // Reset selected services when case type changes
        setValidationErrors(prev => ({ ...prev, caseType: null }));

        // Update API parameters
        setApiParameters(prev => ({
            ...prev,
            case_create: {
                ...prev.case_create,
                case_type: value?.value
            }
        }));
    };

    const handleServiceChange = (serviceId) => {
        const newServices = selectedServices.includes(serviceId)
            ? selectedServices.filter(id => id !== serviceId)
            : [...selectedServices, serviceId];

        setSelectedServices(newServices);

        // Update API parameters
        setApiParameters(prev => ({
            ...prev,
            case_create: {
                ...prev.case_create,
                services: newServices
            }
        }));

        validateApiSelection();
    };

    const validateApiSelection = () => {
        const errors = {};
        
        // Check if at least one API is selected
        if (!selectedApis || selectedApis.length === 0) {
            errors.apis = "At least one API must be selected";
            setValidationErrors(errors);
            return false;
        }

        // Validate parameters for each selected API
        selectedApis.forEach(apiId => {
            if (!apiId) return; // Skip if apiId is null or undefined
            
            const api = availableApis.find(a => a.id === apiId);
            if (api && api.requiredParams) {
                api.requiredParams.forEach(param => {
                    if (apiId === 'case_create') {
                        if (param === 'case_type' && !selectedCaseType) {
                            errors.caseType = "Case type is required";
                        }
                        if (param === 'services' && (!selectedServices || selectedServices.length === 0)) {
                            errors.services = "At least one service type is required";
                        }
                    }
                });
            }
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const generateToken = async () => {
        if (!validateApiSelection()) {
            toastr.error("Error", "Please complete the required selections");
            return;
        }

        try {
            setIsGenerating(true);
            const response = await instance.post(`/user/v1_generateToken/${userId}`, {
                apis: selectedApis.map(apiId => ({
                    api_type: apiId,
                    parameters: apiParameters[apiId] || {}
                }))
            });
            setGeneratedToken(response.data?.data?.api_token);
            toastr.success("Success", "API token generated successfully");
        } catch (error) {
            console.log(error);
            handleError(error);
        } finally {
            setIsGenerating(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedToken);
        toastr.success("Success", "Token copied to clipboard");
    };

    const availableServiceTypes = selectedCaseType ? serviceOptionsByCaseType[selectedCaseType.value] || [] : [];

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <Form>
            <Card className="mb-4 border-0 shadow-sm">
                <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                        <FontAwesomeIcon icon={faServer} className="text-primary fa-2x mr-3" />
                        <div>
                            <h4 className="mb-1">API Documentation</h4>
                            <p className="text-muted mb-0">Select APIs and configure their parameters</p>
                        </div>
                    </div>

                    <div className="api-list">
                        {availableApis.map((api) => (
                            <Card key={api.id} className="mb-3 border-0 shadow-sm">
                                <CardBody className="p-0">
                                    <div
                                        id={api.id}
                                        className="p-3 d-flex align-items-center justify-content-between cursor-pointer"
                                        style={{
                                            backgroundColor: `${api.color}10`,
                                            borderLeft: `4px solid ${api.color}`,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    //   handleApiSelection(api.id);
                                                }}
                                                className="mr-3 ml-3"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <label htmlFor={api.id} onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleApiSelection(api.id);
                                                }} style={{ width: '18px', height: '18px', cursor: 'pointer', border: '1px solid #000', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderColor: selectedApis.includes(api.id) ? api.color : 'red' }}>
                                                    {
                                                        selectedApis.includes(api.id) ? (
                                                            <FontAwesomeIcon icon={faCheck} style={{ color: api.color }} className="fa-lg" />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faTimes} style={{ color: "red" }} className="fa-lg" />
                                                        )
                                                    }
                                                </label>
                                                {/* <Input

                                                    type="checkbox"
                                                    checked={selectedApis.includes(api.id) ? true : false}
                                                    id={api.id}
                                                    onChange={(e) => {
                                                        e.stopPropagation();

                                                        handleApiSelection(api.id);
                                                    }}
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '-12px', display: 'none1' }}
                                                /> */}
                                            </div>
                                            <div
                                                onClick={() => setActiveApi(activeApi === api.id ? null : api.id)}
                                                style={{ cursor: 'pointer' }}
                                                className="d-flex align-items-center"
                                            >
                                                <FontAwesomeIcon icon={api.icon} style={{ color: api.color }} className="fa-lg mr-3" />
                                                <div>
                                                    <h5 className="mb-1">{api.name}</h5>
                                                    <p className="text-muted mb-0">{api.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className="d-flex align-items-center"
                                            onClick={() => setActiveApi(activeApi === api.id ? null : api.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Badge color="light" className="mr-3 px-3 py-2" style={{ color: api.color, border: `1px solid ${api.color}` }}>
                                                {api.method}
                                            </Badge>
                                            <FontAwesomeIcon
                                                icon={activeApi === api.id ? faChevronUp : faChevronDown}
                                                style={{ color: api.color }}
                                            />
                                        </div>
                                    </div>

                                    <UncontrolledCollapse toggler={`#${api.id}`} isOpen={activeApi === api.id}>
                                        <div className="p-4">
                                            {selectedApis.includes(api.id) ? (
                                                <>
                                                    <div className="mb-4">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <FontAwesomeIcon icon={faCode} style={{ color: api.color }} className="mr-2" />
                                                            <h6 className="mb-0">Endpoint</h6>
                                                        </div>
                                                        <div className="bg-light p-3 rounded">
                                                            <code className="text-primary">{api.url}</code>
                                                        </div>
                                                    </div>

                                                    {api.id === "case_create" && (
                                                        <>
                                                            <div className="mb-4">
                                                                <div className="d-flex align-items-center mb-3">
                                                                    <FontAwesomeIcon icon={faCog} style={{ color: api.color }} className="mr-2" />
                                                                    <h6 className="mb-0">Parameters</h6>
                                                                </div>
                                                                <Row>
                                                                    <Col md={6}>
                                                                        <FormGroup>
                                                                            <Label className="font-weight-bold">Case Type</Label>
                                                                            <Select
                                                                                className={`basic-single ${validationErrors.caseType ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="select"
                                                                                value={selectedCaseType}
                                                                                onChange={handleCaseTypeChange}
                                                                                options={caseTypes}
                                                                                placeholder="Select Case Type"
                                                                            />
                                                                            {validationErrors.caseType && (
                                                                                <small className="text-danger">{validationErrors.caseType}</small>
                                                                            )}
                                                                        </FormGroup>
                                                                    </Col>
                                                                    <Col md={6}>
                                                                        <FormGroup>
                                                                            <Label className="font-weight-bold">Service Types</Label>
                                                                            <div className="border rounded p-3 bg-light">
                                                                                {serviceTypesOptions.map((service) =>
                                                                                    availableServiceTypes.includes(service.id) && (
                                                                                        <div key={service.id} className="mb-2 ms-3">
                                                                                            <Label check className="d-flex align-items-center ml-3">
                                                                                                <Input
                                                                                                    type="checkbox"
                                                                                                    checked={selectedServices.includes(service.id)}
                                                                                                    onChange={() => handleServiceChange(service.id)}
                                                                                                    className="mr-2"
                                                                                                />
                                                                                                <span>{service.label}</span>
                                                                                                {selectedServices.includes(service.id) && (
                                                                                                    <FontAwesomeIcon icon={faCheck} className="text-success ml-2" />
                                                                                                )}
                                                                                            </Label>
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                                {validationErrors.services && (
                                                                                    <small className="text-danger">{validationErrors.services}</small>
                                                                                )}
                                                                            </div>
                                                                        </FormGroup>
                                                                    </Col>
                                                                </Row>
                                                            </div>

                                                            <div className="mb-4">
                                                                <div className="d-flex align-items-center mb-3">
                                                                    <FontAwesomeIcon icon={faFileAlt} style={{ color: api.color }} className="mr-2" />
                                                                    <h6 className="mb-0">Request Payload</h6>
                                                                </div>
                                                                <Card className="border-0 shadow-sm">
                                                                    <CardBody className="p-0">
                                                                        <div className="bg-light p-4">
                                                                            <div className="d-flex align-items-center mb-3">
                                                                                <FontAwesomeIcon icon={faInfoCircle} className="text-info mr-2" />
                                                                                <small className="text-muted">Fields marked with // Required are mandatory</small>
                                                                            </div>
                                                                            <pre className="mb-0 bg-dark text-light p-4 rounded">
                                                                                {`{
  "title": "string",
  "first_name": "string", // Required
  "last_name": "string", // Required
  "mobile_no": "string", // Required
  "address": "string",
  "email": "string",
  "ni_number": "string",
  "dob": "date",
  "occupation": "string",
  "insurer": "string",
  "policy_number": "string",

  "client_vehicle": {
    "vehicle_reg": "string", // Required
    "make_model": "string",
    "make_year": "integer",
    "registered_keeper": "string",
    "damage_detail": "string",
    "color": "string",
    "insurance": "string",
    "insurance_type": "string", // Third Party, Fully Comprehensive, Third Party Fire and Theft
    "insurer": "string",
    "policy_number": "string"
  },

  "accident_injury": {
    "accident_date": "date",
    "accident_time": "time",
    "accident_location": "string",
    "accident_description": "string"
  },

  "third_party": {
    "first_name": "string",
    "last_name": "string",
    "address": "string",
    "email": "string",
    "contact_number": "string",
    "vehicle_reg": "string", // Required
    "colour": "string",
    "make_model": "string",
    "insurance_policy_number": "string",
    "insurer": "string"
  }
}`}
                                                                            </pre>
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="text-center p-4">
                                                    <FontAwesomeIcon icon={faLock} className="text-muted fa-2x mb-3" />
                                                    <p className="text-muted mb-0">Enable this API to view and configure its parameters</p>
                                                </div>
                                            )}
                                        </div>
                                    </UncontrolledCollapse>
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                </CardBody>
            </Card>

            <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-3">
                        <FontAwesomeIcon icon={faLock} className="text-primary mr-2" />
                        <h5 className="mb-0">API Token</h5>
                    </div>
                    <Row>
                        <Col md={9}>
                            <InputGroup>
                                <Input
                                    type="text"
                                    readOnly
                                    value={generatedToken}
                                    placeholder="Generated API Token"
                                    className="bg-light"
                                />
                                <InputGroupAddon addonType="append">
                                    <InputGroupText>
                                        <Button
                                            color="link"
                                            className="p-0"
                                            onClick={copyToClipboard}
                                            disabled={!generatedToken}
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </Button>
                                    </InputGroupText>
                                </InputGroupAddon>
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Button
                                color="primary"
                                block
                                onClick={generateToken}
                                disabled={isGenerating}
                                className="shadow-sm"
                            >
                                {isGenerating && <Spinner size="sm" className="mr-2" />}
                                Generate Token
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Form>
    );
});

export default UserAPI; 